import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DirectionsBusOutlinedIcon from '@mui/icons-material/DirectionsBusOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

import { AppContext } from '../../context/context';
import { fetchRulesSettings, fetchVehicleTags, upsertVehicleTags } from '../../features/rulesEngine/api';
import { flattenFleetsToVehicleTagSeeds } from '../../features/rulesEngine/fleetVehicleTags';
import RulesSectionCard from './RulesSectionCard';

interface VehicleRow {
  vehicle_id: string;
  capability_tags: string[];
}

/**
 * Displays the registered vehicle fleet with their capability tags.
 * Tags drive rostering eligibility: a vehicle is only assigned to a demand
 * block if it carries all required tags for that route.
 */
export default function VehiclesPanel() {
  const app = useContext(AppContext);
  const fleets = app?.global?.fleets || [];
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [tagVocabulary, setTagVocabulary] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const [newId, setNewId] = useState('');
  const [newTags, setNewTags] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const reload = useCallback(async () => {
    const [rows, settings] = await Promise.all([fetchVehicleTags(), fetchRulesSettings()]);
    setVehicles(rows);
    setTagVocabulary(settings.tag_vocabulary ?? []);
  }, []);

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : 'Failed to load vehicles'));
  }, [reload]);

  const parseTags = (raw: string): string[] =>
    raw
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean);

  const onImportFromFleets = async () => {
    setImporting(true);
    setError(null);
    setSuccess(null);
    try {
      const seeds = flattenFleetsToVehicleTagSeeds(fleets);
      if (!seeds.length) {
        setError('No vehicles found in map fleets. Add vehicles in the Fleet drawer first.');
        return;
      }
      const failed: string[] = [];
      for (const seed of seeds) {
        try {
          await upsertVehicleTags(seed.vehicle_id, seed.capability_tags);
        } catch {
          failed.push(seed.vehicle_id);
        }
      }
      await reload();
      if (failed.length) {
        setError(
          `Imported with errors for: ${failed.join(', ')} (need rules_engine_admin for tag writes).`
        );
      } else {
        setSuccess(`Imported ${seeds.length} vehicle${seeds.length === 1 ? '' : 's'} from map fleets.`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fleet import failed');
    } finally {
      setImporting(false);
    }
  };

  const onAdd = async () => {
    if (!newId.trim()) return;
    setError(null);
    setSuccess(null);
    try {
      await upsertVehicleTags(newId.trim(), parseTags(newTags));
      setNewId('');
      setNewTags('');
      setSuccess('Vehicle saved.');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const startEdit = (v: VehicleRow) => {
    setEditingId(v.vehicle_id);
    setEditDraft(v.capability_tags.join(', '));
  };

  const commitEdit = async (vehicle_id: string) => {
    setError(null);
    try {
      await upsertVehicleTags(vehicle_id, parseTags(editDraft));
      setEditingId(null);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const cancelEdit = () => setEditingId(null);

  return (
    <Box data-testid="rules.vehicles.panel" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Vehicles
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Capability tags control which routes a vehicle can cover. A vehicle is only eligible for a
          demand block if it carries all tags that route requires.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <RulesSectionCard
        icon={<SyncAltOutlinedIcon />}
        title="Import from map fleets"
        description="Pull vehicle IDs from the Fleet drawer and seed tags (wheelchair → accessible, coach names → coach)."
        action={
          <Button
            variant="outlined"
            disabled={importing}
            onClick={() => void onImportFromFleets()}
            startIcon={<SyncAltOutlinedIcon />}
            sx={{ whiteSpace: 'nowrap' }}
            data-testid="rules.vehicles.import-fleets"
          >
            {importing ? 'Importing…' : 'Import from map fleets'}
          </Button>
        }
      >
        <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />}>
          Map fleets live in the sidebar Fleet drawer. Import overwrites tags for matching vehicle IDs;
          tag writes need the <strong>rules_engine_admin</strong> permission.
        </Alert>
      </RulesSectionCard>

      <RulesSectionCard
        icon={<LocalOfferOutlinedIcon />}
        title="Known tags"
        description="Vocabulary from Rule sets. Use these when tagging vehicles so rostering can match routes."
      >
        {tagVocabulary.length > 0 ? (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {tagVocabulary.map((t) => (
              <Chip
                key={t}
                label={t}
                size="small"
                variant="outlined"
                icon={<LocalOfferOutlinedIcon />}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No vocabulary yet. Add tags under Rule sets, then assign them here.
          </Typography>
        )}
      </RulesSectionCard>

      <RulesSectionCard
        icon={<AddCircleOutlineIcon />}
        title="Register vehicle"
        description="Add a vehicle ID and optional capability tags (comma or space separated)."
      >
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', md: 'minmax(140px, 200px) 1fr auto' },
            alignItems: 'start',
          }}
        >
          <TextField
            size="small"
            label="Vehicle ID"
            value={newId}
            onChange={(e) => setNewId(e.target.value)}
            fullWidth
            data-testid="rules.vehicles.id-input"
          />
          <TextField
            size="small"
            label="Capability tags"
            placeholder="e.g. accessible, coach"
            value={newTags}
            onChange={(e) => setNewTags(e.target.value)}
            fullWidth
            data-testid="rules.vehicles.tags-input"
          />
          <Button
            variant="contained"
            onClick={() => void onAdd()}
            disabled={!newId.trim()}
            startIcon={<DirectionsBusOutlinedIcon />}
            sx={{ height: 40, whiteSpace: 'nowrap' }}
            data-testid="rules.vehicles.add-btn"
          >
            Add vehicle
          </Button>
        </Box>
      </RulesSectionCard>

      <RulesSectionCard
        icon={<Inventory2OutlinedIcon />}
        title="Registered vehicles"
        description="Edit tags inline. Empty tags mean the vehicle can only cover routes with no required tags."
        action={
          vehicles.length > 0 ? (
            <Chip
              size="small"
              variant="outlined"
              icon={<DirectionsBusOutlinedIcon />}
              label={`${vehicles.length} vehicle${vehicles.length === 1 ? '' : 's'}`}
            />
          ) : null
        }
      >
        <Table size="small" data-testid="rules.vehicles.table">
          <TableHead>
            <TableRow>
              <TableCell>Vehicle ID</TableCell>
              <TableCell>Capability tags</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vehicles.map((v) => (
              <TableRow key={v.vehicle_id}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{v.vehicle_id}</TableCell>
                <TableCell>
                  {editingId === v.vehicle_id ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        autoFocus
                        sx={{ minWidth: 220 }}
                        data-testid={`rules.vehicles.edit-tags-${v.vehicle_id}`}
                      />
                      <Tooltip title="Save">
                        <IconButton
                          size="small"
                          onClick={() => void commitEdit(v.vehicle_id)}
                          aria-label="Save tags"
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton size="small" onClick={cancelEdit} aria-label="Cancel edit">
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {v.capability_tags.length ? (
                        v.capability_tags.map((t) => (
                          <Chip key={t} label={t} size="small" variant="outlined" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          no tags
                        </Typography>
                      )}
                    </Stack>
                  )}
                </TableCell>
                <TableCell align="right">
                  {editingId !== v.vehicle_id && (
                    <Tooltip title="Edit tags">
                      <IconButton
                        size="small"
                        onClick={() => startEdit(v)}
                        aria-label={`Edit tags for ${v.vehicle_id}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!vehicles.length && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    No vehicles registered yet. Import from map fleets, add one above, or use Load demo
                    routes in Rostering.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </RulesSectionCard>
    </Box>
  );
}
